import { Box, Button, Card, CardContent, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "../components/PageTitle";
import { isPrettyChitty } from "../../shared/util/branding";

const sisterSiteUrl = import.meta.env.VITE_SISTER_SITE_URL;

interface FeatureCardProps {
  title: string;
  description: string;
  icon: string;
}

function FeatureCard({ title, description, icon }: FeatureCardProps) {
  const theme = useTheme();
  return (
    <Card
      sx={{
        background: theme.palette.background.paper,
        height: "100%",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 24px rgba(140, 0, 255, 0.3)`,
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Box
            sx={{
              fontSize: "2.5rem",
              width: 60,
              height: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
              borderRadius: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {description}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

const features: FeatureCardProps[] = [
  {
    title: "Discord Integration",
    description:
      "Seamless matchmaking and real-time notifications through Discord. Get notified when it's your turn, find opponents, and manage games without leaving your server.",
    icon: "💬",
  },
  {
    title: "Fast Loading & Mobile Friendly",
    description:
      "Optimized for performance on any device. Play on your phone, tablet, or desktop with a responsive interface that adapts to your screen.",
    icon: "📱",
  },
  {
    title: "3D Realistic Graphics",
    description:
      "Beautiful 3D rendering brings your board games to life. Realistic lighting, shadows, and textures create an immersive tabletop experience.",
    icon: "🎮",
  },
  {
    title: "Adaptive Panel System",
    description:
      "Tabbed panels and flexible grid layouts ensure your game is playable on any screen size. Arrange your view exactly how you want it.",
    icon: "📐",
  },
  {
    title: "Undo & Confirm Built-in",
    description:
      "Made a mistake? No problem. Built-in undo functionality and action confirmation keeps games fair and frustration-free.",
    icon: "↩️",
  },
  {
    title: "Gallery Modes",
    description:
      "Long-touch selection for objects, expandable hand views for cards, and action galleries make complex game states easy to navigate.",
    icon: "🖼️",
  },
  {
    title: "Dynamic Texture Engine",
    description:
      "Real-time texture generation for cards, tokens, and game pieces. Create dynamic visuals that update as the game state changes.",
    icon: "🎨",
  },
  {
    title: "Rapid Development",
    description:
      "Build complete games in hours, not months. The TypeScript SDK handles rendering, networking, and state management so you can focus on game logic.",
    icon: "⚡",
  },
  {
    title: "Full Rewind & Replay",
    description:
      "Review any moment in your game with fast, frame-by-frame rewind. Perfect for learning, analyzing strategies, or settling disputes.",
    icon: "⏪",
  },
];

export default function Features() {
  const theme = useTheme();
  const navigate = useNavigate();
  return (
    <Box>
      <PageTitle
        title="Features"
        subTitle="Everything you need to bring board games online"
      />

      <Typography variant="body1" sx={{ pb: 4, maxWidth: 600 }}>
        Pretty Chitty is a complete platform for developing and playing strategy
        board games online. Here's what makes it special.
      </Typography>

      <Grid2 container spacing={3}>
        {features.map((feature) => (
          <Grid2 key={feature.title} size={{ xs: 12, sm: 6, md: 4 }}>
            <FeatureCard {...feature} />
          </Grid2>
        ))}
      </Grid2>

      <Box sx={{ mt: 6, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Ready to build your own game?
        </Typography>
        {isPrettyChitty() ? (
          <>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 3, maxWidth: 600, mx: "auto" }}
            >
              Pretty Chitty hosts curated games that run on secure servers. For developers who want to
              build and publish their own games, check out Really Chitty—our open platform where your
              game code runs in players' browsers. No approval process, just build and share.
            </Typography>
            {sisterSiteUrl && (
              <Button
                variant="contained"
                size="large"
                href={`${sisterSiteUrl}/spa/develop`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  fontWeight: 600,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                Start Building
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/develop")}
            sx={{
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              fontWeight: 600,
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            Start Developing
          </Button>
        )}
      </Box>
    </Box>
  );
}
